import * as React from "react";
import * as _ from "lodash";
import * as moment from "moment";
import * as yup from "yup";
import { DateInput } from "../../components/inputs/DateInput";
import { PluginTemplates } from "@falcon/plugin-falcon-journey";

const FormGroup: React.FC<React.HTMLProps<HTMLDivElement>> = (props) => (
    <div {...props} className="form-group"/>
)

const Label: React.FC<React.HTMLProps<HTMLLabelElement>> = (props) => (
    <label {...props} className=""/>
)

const InputGroup: React.FC<React.HTMLProps<HTMLDivElement>> = (props) => (
    <div {...props} className="input-group"/>
)

const Input: React.FC<{isValid?: boolean} & React.HTMLProps<HTMLInputElement>> = (props) => (
    <input
        {..._.omit(props, "isValid")}
        className={`form-control ${props.isValid === false ? "is-invalid" : ""}`}
    />
)

const ErrorMessage: React.FC<React.HTMLProps<HTMLDivElement>> = (props) => (
    <div {...props} className="invalid-feedback d-block"/>
)

interface IProps {
    location?: any
    match?: any
}

interface Form {
    hasBeenSubmitted: boolean
    quoteId: string
    email: string
    dob: string
}

interface IState {
    form: Form
    formErrors: { [key: string]: string }
}

export class RetrievalDetailsPage extends React.Component<IProps, IState> {

    private readonly formSchema: any;

    private readonly pluginTemplates = new PluginTemplates();

    constructor(props: IProps) {
        super(props);

        const requiredValidation = {
            conditionField: "hasBeenSubmitted",
            conditionOptions: {
                is: true,
                then: yup.string().required("This field is mandatory")
            }
        }
        this.formSchema = yup.object({
            hasBeenSubmitted: yup.boolean(),
            quoteId: yup
                .string()
                .when(requiredValidation.conditionField, requiredValidation.conditionOptions)
                .matches(/^$|^[a-fA-F0-9]{24}$/, {message: "Invalid ID format"}),
            email: yup
                .string()
                .when(requiredValidation.conditionField, requiredValidation.conditionOptions)
                .email("Invalid email address"),
            dob: yup
                .string()
                .when(requiredValidation.conditionField, requiredValidation.conditionOptions)
                .test("customDate format", "Invalid date format. Example: 06/02/1981", (dob: string) =>
                    !dob || dob.length === 10 && moment(dob, "yyyy-MM-DD", true).isValid()
                )
                .test("customDate not in future", "Date can't be in the future", (dob: string) =>
                    !dob || moment(dob).isSameOrBefore(moment())
                )
                .test("customDate year minimum", "Year can't be less than 1900", (dob: string) =>
                    !dob || moment(dob).year() >= 1900
                )
        });

        this.state = {
            form: {
                hasBeenSubmitted: false,
                quoteId: "",
                email: "",
                dob: ""
            },
            formErrors: {},
        };
    }

    public componentDidMount() {
        this.pluginTemplates.setPageTitle("Quote retrieve details");

        window.scrollTo(0, 0);

        const url = new URL(window.location.href);
        const quoteId = url.searchParams.get("quote_id");
        if (quoteId) {
            const form = {...this.state.form};
            form.quoteId = quoteId;
            this.setState({form});
        }
    }

    public render() {
        const { formErrors } = this.state;
        return (
            <React.Fragment>
                {this.pluginTemplates.renderHeader()}
                <div className="main-plugin quote-retrieval-details-page">
                    <div className="text-group title plugin">
                        <h1>
                            Quote retrieval
                        </h1>
                    </div>
                    <div className="new-quote-container plugin">
                        <form onSubmit={this.handleSubmit}>
                            <h2 className="text-left">Please, enter your details...</h2>
                            <div className="inner-content">
                                <FormGroup>
                                    <Label htmlFor="quoteId">Quote ID:</Label>
                                    <InputGroup>
                                        <Input
                                            type="text"
                                            id="quoteId"
                                            name="quoteId"
                                            value={this.state.form.quoteId}
                                            isValid={!formErrors.quoteId}
                                            onChange={this.handleChange}
                                        />
                                        {formErrors.quoteId && <ErrorMessage>{formErrors.quoteId}</ErrorMessage>}
                                    </InputGroup>
                                </FormGroup>
                                <FormGroup>
                                    <Label htmlFor="email">Email address:</Label>
                                    <InputGroup>
                                        <Input
                                            type="text"
                                            id="email"
                                            name="email"
                                            value={this.state.form.email}
                                            isValid={!formErrors.email}
                                            onChange={this.handleChange}
                                        />
                                        {formErrors.email && <ErrorMessage>{formErrors.email}</ErrorMessage>}
                                    </InputGroup>
                                </FormGroup>
                                <FormGroup>
                                    <Label htmlFor="dob">Date of birth:</Label>
                                    <InputGroup>
                                        <DateInput
                                            id="dob"
                                            name="dob"
                                            value={this.state.form.dob}
                                            isValid={!formErrors.dob}
                                            onChange={this.handleChange}
                                        />
                                        {formErrors.dob && <ErrorMessage>{formErrors.dob}</ErrorMessage>}
                                    </InputGroup>
                                </FormGroup>
                            </div>
                            <button
                                type="submit"
                                id="quoteRetrieve"
                                className={`btn btn-primary ${this.isFormValid() ? "" : "btn-disabled"} btn-next plugin btn-center`}
                            >Retrieve quote</button>
                        </form>
                    </div>
                </div>
                {this.pluginTemplates.renderFooter()}
            </React.Fragment>
        );
    }

    private handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const form = {...this.state.form};
        form.hasBeenSubmitted = true;
        this.setState(
            {form},
            async () => {
                await this.validateForm();
                if (this.isFormValid()) {
                    window.location.href = `/retrieveQuote/products/AnnualMotorInsurance/process/nb/interaction/${this.state.form.quoteId}/language/en-GB/email/${encodeURIComponent(this.state.form.email)}/dob/${this.state.form.dob}`;
                }
            }
        );
    }

    private handleChange = (event: React.FormEvent<HTMLInputElement>) => {
        const form = {...this.state.form};
        const target = event.target as HTMLInputElement;
        form[target.name] = target.value;
        this.setState({form}, this.validateForm);
    }

    private isFormValid = (): boolean => {
        return _.isEmpty(this.state.formErrors);
    }

    private validateForm = () => {
        return this.formSchema
            .validate(this.state.form, {abortEarly: false})
            .then(() => this.setState({formErrors: {}}))
            .catch((caughtErrors: any) => {
                const formErrors = {};
                caughtErrors.inner.reverse().forEach((innerError: any) => {
                    formErrors[innerError.path] = innerError.message
                })
                this.setState({formErrors})
            });
    }
}



// WEBPACK FOOTER //
// ./src/pages/quote/RetrievalDetailsPage.tsx